<template>
    <div class="receipt-page">
      <InventoryHeaderTab />
  
      <!-- Page Header -->
      <div class="page-header">
        <div class="page-title-wrap">
          <router-link :to="$url('/all-purchases')" class="back-link">
            <font-awesome-icon icon="angle-left" /> Back
          </router-link>
          <div>
            <h5 class="page-title">{{ purchase?.batch_code ?? 'Purchase Detail' }}</h5>
            <p class="page-sub">Purchase receipt and line items</p>
          </div>
        </div>
  
        <div class="page-actions" v-if="purchase">
          <button class="btn-outline" @click="createPDF(purchase.batch_code)">
            <font-awesome-icon icon="download" /> Download PDF
          </button>
          <div class="dropdown">
            <button class="btn-primary" @click="printOpen = !printOpen">
              <font-awesome-icon icon="print" /> Print
              <font-awesome-icon icon="chevron-down" class="ms-1" />
            </button>
            <ul class="dropdown-menu" :class="{ show: printOpen }">
              <li>
                <router-link class="dropdown-item" :to="$url('/purchase-receipt/' + $route.params.id + '?size=58')">
                  <font-awesome-icon icon="receipt" class="me-2 text-muted-cell" /> Print 58mm
                </router-link>
              </li>
              <li>
                <router-link class="dropdown-item" :to="$url('/purchase-receipt/' + $route.params.id + '?size=80')">
                  <font-awesome-icon icon="receipt" class="me-2 text-muted-cell" /> Print 80mm
                </router-link>
              </li>
              <li>
                <router-link class="dropdown-item" :to="$url('/purchase-receipt/' + $route.params.id + '?size=a4')">
                  <font-awesome-icon icon="file-alt" class="me-2 text-muted-cell" /> Print A4
                </router-link>
              </li>
            </ul>
          </div>
        </div>
      </div>
  
      <!-- Loading -->
      <div v-if="loading" class="state-block">
        <div class="spinner-border text-primary" style="width:3rem;height:3rem;" role="status"></div>
        <p class="mt-3 text-muted-cell">Loading purchase...</p>
      </div>
  
      <!-- Empty -->
      <div v-else-if="!purchase" class="state-block">
        <div class="state-icon"><font-awesome-icon icon="shopping-bag" /></div>
        <p class="state-title">Purchase not found</p>
        <p class="state-sub">This purchase record could not be loaded.</p>
      </div>
  
      <!-- Receipt card -->
      <div v-else class="receipt-card" id="pdf">
  
        <!-- Meta row -->
        <div class="meta-grid">
          <div class="meta-item">
            <span class="meta-label">Date</span>
            <span class="meta-value">{{ dateTime(purchase.date) }}</span>
          </div>
          <div class="meta-item">
            <span class="meta-label">Purchase No.</span>
            <span class="meta-value">
              <span class="batch-code">{{ purchase.batch_code }}</span>
            </span>
          </div>
          <div class="meta-item">
            <span class="meta-label">Store</span>
            <span class="meta-value">{{ purchase.store?.store_name ?? '—' }}</span>
          </div>
          <div class="meta-item">
            <span class="meta-label">Supplier</span>
            <span class="meta-value">{{ purchase.supplier?.supplier_name ?? '—' }}</span>
          </div>
          <div class="meta-item">
            <span class="meta-label">Purchased By</span>
            <span class="meta-value">{{ purchase.user?.fullname ?? '—' }}</span>
          </div>
        </div>
  
        <div class="section-divider"></div>
  
        <!-- Empty items -->
        <div v-if="purchase_items.length === 0" class="state-block" style="padding:40px 24px;">
          <div class="state-icon"><font-awesome-icon icon="box-open" /></div>
          <p class="state-title">No items on this purchase</p>
        </div>
  
        <!-- Items table -->
        <div v-else class="table-wrap">
          <table class="data-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Product</th>
                <th>Variant</th>
                <th class="text-right">Cost Price</th>
                <th class="text-right">Quantity</th>
                <th class="text-right">Total ({{ company?.currency?.symbol }})</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in purchase_items" :key="item.id">
  
                <td class="text-muted-cell">{{ index + 1 }}</td>
  
                <td class="fw-500">{{ item.product?.product_name }}</td>
  
                <td>
                  <span v-if="item.variant?.variant_name" class="variant-tag">
                    {{ item.variant.variant_name }}
                  </span>
                  <span v-else class="text-muted-cell">—</span>
                </td>
  
                <td class="text-right text-muted-cell">
                  {{ company?.currency?.symbol }}{{ $filters.formatMoney(item.product?.cost_price) }}
                </td>
  
                <td class="text-right fw-500">{{ item.quantity }}</td>
  
                <td class="text-right fw-bold">
                  {{ company?.currency?.symbol }}{{ $filters.formatMoney(item.cost_price * item.quantity) }}
                </td>
  
              </tr>
            </tbody>
          </table>
        </div>
  
        <!-- Totals -->
        <div class="totals-wrap" v-if="purchase_items.length > 0">
          <div class="total-row">
            <span class="total-label">Subtotal</span>
            <span class="total-value">{{ company?.currency?.symbol }}{{ $filters.formatMoney(totalPrice) }}</span>
          </div>
          <div class="total-row total-row--grand">
            <span class="total-label">Grand Total</span>
            <span class="total-value fw-bold">{{ company?.currency?.symbol }}{{ $filters.formatMoney(totalPrice) }}</span>
          </div>
        </div>
  
      </div>
    </div>
  </template>
  
  <script>
  import axios from 'axios';
  import jsPDF from 'jspdf';
  import html2canvas from 'html2canvas';
  import { useToast } from 'vue-toastification';
  import { reusableMixin } from '../../../lib/reusableMixin';
  
  export default {
    name: 'PurchaseReceipt',
  
    mixins: [reusableMixin],
  
    data() {
      return {
        toast:          useToast(),
        purchase:       null,
        purchase_items: [],
        loading:        false,
        printOpen:      false,
      };
    },
  
    computed: {
      totalPrice() {
        return this.purchase_items.reduce((sum, item) => {
          return sum + (parseFloat(item.cost_price) || 0) * (item.quantity || 0);
        }, 0).toFixed(2);
      },
    },
  
    methods: {
      createPDF(batch_code) {
        const element = document.getElementById('pdf');
        if (!element) return;
  
        html2canvas(element, { scale: 2 }).then(canvas => {
          const imgData = canvas.toDataURL('image/png');
          const pdf     = new jsPDF({ unit: 'pt', orientation: 'l', lineHeight: 1.2 });
          const pdfW    = pdf.internal.pageSize.getWidth();
          const pdfH    = (canvas.height * pdfW) / canvas.width;
  
          pdf.addImage(imgData, 'PNG', 0, 0, pdfW, pdfH);
          pdf.save(`${batch_code}.pdf`);
        });
      },
  
      getPurchase() {
        this.loading = true;
        axios.get(`/inventory/purchase/${this.$route.params.id}`)
          .then(({ data }) => {
            this.purchase       = data.data;
            this.purchase_items = data.data.purchase_item ?? [];
          })
          .catch(() => { this.toast.error('Could not load purchase. Please try again.'); })
          .finally(() => { this.loading = false; });
      },
    },
  
    created() {
      this.getPurchase();
      this.getCompany();
    },
  
    mounted() {
      document.addEventListener('click', (e) => {
        if (!e.target.closest('.dropdown')) this.printOpen = false;
      });
    },
  };
  </script>
  
  <style scoped>
  /* ── Page ────────────────────────────────────────────────────────────── */
  .receipt-page { display:flex; flex-direction:column; gap:20px; }
  
  /* ── Header ──────────────────────────────────────────────────────────── */
  .page-header     { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:14px; }
  .page-title-wrap { display:flex; align-items:flex-start; gap:14px; }
  .page-title      { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
  .page-sub        { font-size:13px; color:#7a90b0; margin:0; }
  .page-actions    { display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
  
  .back-link { display:inline-flex; align-items:center; gap:6px; font-size:13px; font-weight:600; color:#7a90b0; padding:6px 10px; border-radius:7px; border:1px solid #e4eaf2; background:#fff; transition:all .15s; text-decoration:none; white-space:nowrap; align-self:flex-start; margin-top:2px; }
  .back-link:hover { background:#f4f7fb; color:#1a2b45; text-decoration:none; }
  
  .btn-outline { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#1a2b45; border:1px solid #dde5f0; font-size:13px; font-weight:500; padding:8px 14px; border-radius:8px; cursor:pointer; transition:background .15s; white-space:nowrap; }
  .btn-outline:hover { background:#f4f7fb; }
  .btn-primary { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:8px 16px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; white-space:nowrap; }
  .btn-primary:hover { background:#003b8e; }
  .ms-1 { margin-left:4px; }
  
  .dropdown         { position:relative; }
  .dropdown-menu    { position:absolute; top:calc(100% + 6px); right:0; background:#fff; border:1px solid #e4eaf2; border-radius:10px; box-shadow:0 8px 24px rgba(0,0,0,.1); padding:6px; min-width:170px; z-index:200; display:none; }
  .dropdown-menu.show { display:block; }
  .dropdown-item    { display:flex; align-items:center; gap:8px; padding:9px 12px; font-size:13px; border-radius:7px; color:#1a2b45; cursor:pointer; text-decoration:none; transition:background .12s; }
  .dropdown-item:hover { background:#f4f7fb; text-decoration:none; color:#1a2b45; }
  .me-2 { margin-right:8px; }
  
  /* ── States ──────────────────────────────────────────────────────────── */
  .state-block { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:64px 24px; text-align:center; }
  .state-icon  { width:60px; height:60px; border-radius:16px; background:rgba(0,74,173,.08); color:#004AAD; display:flex; align-items:center; justify-content:center; font-size:24px; margin-bottom:16px; }
  .state-title { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
  .state-sub   { font-size:13px; color:#7a90b0; margin:0; }
  .text-muted-cell { color:#4a5e78; }
  .mt-3 { margin-top:12px; }
  
  /* ── Receipt card ────────────────────────────────────────────────────── */
  .receipt-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
  
  /* ── Meta grid ───────────────────────────────────────────────────────── */
  .meta-grid { display:flex; flex-wrap:wrap; gap:0; padding:24px 28px; }
  .meta-item { display:flex; flex-direction:column; gap:4px; flex:1; min-width:160px; padding:0 20px 0 0; }
  .meta-item:not(:last-child) { border-right:1px solid #f0f4f8; margin-right:20px; }
  .meta-label { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; }
  .meta-value { font-size:13px; font-weight:600; color:#1a2b45; }
  
  /* ── Batch code ──────────────────────────────────────────────────────── */
  .batch-code { font-family:monospace; font-size:12px; font-weight:600; color:#1a2b45; background:#f4f7fb; border:1px solid #e4eaf2; border-radius:6px; padding:2px 8px; }
  
  /* ── Divider ─────────────────────────────────────────────────────────── */
  .section-divider { border:none; border-top:1px solid #e4eaf2; }
  
  /* ── Table ───────────────────────────────────────────────────────────── */
  .table-wrap  { overflow-x:auto; }
  .data-table  { width:100%; border-collapse:collapse; }
  .data-table thead tr { background:#f8fafc; }
  .data-table thead th { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:12px 16px; border-bottom:1px solid #e4eaf2; white-space:nowrap; }
  .data-table tbody tr { border-bottom:1px solid #f0f4f8; transition:background .12s; }
  .data-table tbody tr:last-child { border-bottom:none; }
  .data-table tbody tr:hover { background:#f8fafc; }
  .data-table tbody td { padding:12px 16px; font-size:13px; vertical-align:middle; }
  .text-right { text-align:right; }
  .fw-500     { font-weight:500; }
  .fw-bold    { font-weight:700; }
  
  /* ── Variant tag ─────────────────────────────────────────────────────── */
  .variant-tag { display:inline-block; background:#f4f7fb; color:#3d5068; border:1px solid #e4eaf2; border-radius:5px; padding:2px 8px; font-size:11.5px; font-weight:600; }
  
  /* ── Totals ──────────────────────────────────────────────────────────── */
  .totals-wrap { display:flex; flex-direction:column; align-items:flex-end; gap:8px; padding:20px 28px 24px; border-top:1px solid #e4eaf2; }
  .total-row   { display:flex; gap:48px; justify-content:flex-end; font-size:13px; }
  .total-row--grand { font-size:16px; padding-top:10px; border-top:2px solid #e4eaf2; margin-top:4px; }
  .total-label { color:#7a90b0; min-width:100px; text-align:right; }
  .total-value { color:#1a2b45; min-width:120px; text-align:right; }
  
  /* ── Responsive ──────────────────────────────────────────────────────── */
  @media (max-width:768px) {
    .page-header, .page-title-wrap { flex-direction:column; align-items:stretch; }
    .meta-grid   { flex-direction:column; }
    .meta-item   { border-right:none !important; margin-right:0 !important; border-bottom:1px solid #f0f4f8; padding:0 0 14px; margin-bottom:14px; }
    .meta-item:last-child { border-bottom:none; margin-bottom:0; padding-bottom:0; }
    .page-actions { width:100%; }
  }
  
  @media print {
    .page-header { display:none; }
    .receipt-card { border:none; box-shadow:none; }
  }
  </style>